// components/sections/SkillCard.js
import { Draggable } from "@hello-pangea/dnd";
import { ChevronDown, ChevronUp, Edit3, GripVertical, Trash2, Hammer, Check } from 'lucide-react';
import { useState } from 'react';

const BallRating = ({ skillName, rating, onRate }) => {
  const [hoverRating, setHoverRating] = useState(0);

  const getBallColor = (ballIndex, currentRating) => {
    if (ballIndex <= currentRating) {
      if (currentRating <= 2) return 'bg-red-400';
      if (currentRating <= 3) return 'bg-orange-400';
      return 'bg-teal-600';
    }
    return 'bg-gray-300';
  };

  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((ball) => (
        <button
          key={ball}
          className={`w-3 h-3 rounded-full transition-all duration-150 hover:scale-110 ${getBallColor(ball, hoverRating || rating || 0)}`}
          onMouseEnter={() => setHoverRating(ball)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={() => onRate && onRate(skillName, ball)}
          title={`Noter ${ball}/5`}
        />
      ))}
      <span className="text-xs text-gray-500 ml-2">
        {(rating || 0)}/5
      </span>
    </div>
  );
};

const DifficultyBadge = ({ niveau, skillName }) => {
  const getHammerCount = (niveau) => {
    if (!niveau) return 1;
    
    switch (niveau) {
      case 'Débutant': return 1;
      case 'Intermédiaire': return 2;
      case 'Avancé': return 3;
      case 'Expert': return 4;
      default: return 1;
    }
  };

  const hammerCount = getHammerCount(niveau);

  return (
    <div className="flex items-center justify-start w-16">
      {Array.from({ length: hammerCount }, (_, index) => (
        <Hammer key={index} className="w-3 h-3 text-gray-500" />
      ))}
    </div>
  );
};

const SkillDetails = ({ skillName, data }) => {
  if (!data || (!data.description && !data.training_link && !data.certification)) return null;

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="space-y-2 text-sm">
        {data.description && (
          <div>
            <p className="text-gray-700">{data.description}</p>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.training_link && (
            <div className="flex items-center space-x-2">
              <span className="text-gray-600 text-xs">Formation:</span>
              <a 
                href={data.training_link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[#18574F] hover:underline truncate text-xs"
              >
                Lien
              </a>
            </div>
          )}
          
          {data.certification && (
            <div className="flex items-center space-x-2">
              <span className="text-gray-600 text-xs">Certification:</span>
              <span className="text-gray-700 text-xs">{data.certification}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SkillCard = ({
  skill,
  index,
  skillData,
  isExpanded,
  isSelected,
  isSelectionMode,
  isDragDisabled,
  draggedItem,
  sortBy,
  onToggleExpanded,
  onToggleSelection,
  onRate,
  onEdit,
  onRemove
}) => {
  return (
    <Draggable key={skill} draggableId={skill} index={index} isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`group bg-white rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
            snapshot.isDragging
              ? 'border-[#18574F] shadow-lg rotate-2 scale-105'
              : draggedItem === skill
              ? 'border-[#18574F]/50'
              : isSelected
              ? 'border-[#18574F] bg-[#18574F]/5'
              : 'border-gray-200 hover:border-[#18574F]/30'
          } ${(sortBy !== 'none' && !isSelectionMode) ? 'opacity-75' : ''}`}
          style={{
            ...provided.draggableProps.style,
            transform: snapshot.isDragging
              ? `${provided.draggableProps.style?.transform} rotate(2deg)`
              : provided.draggableProps.style?.transform
          }}
        >
          <div className="flex items-center justify-between p-2">
            <div className="flex items-center space-x-2 flex-1">
              {/* Checkbox de sélection ou handle de drag */}
              {isSelectionMode ? (
                <button
                  onClick={() => onToggleSelection(skill)}
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'bg-[#18574F] border-[#18574F] text-white'
                      : 'border-gray-300 hover:border-[#18574F]'
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3" />}
                </button>
              ) : (
                <div
                  {...provided.dragHandleProps}
                  className={`text-gray-400 hover:text-[#18574F] transition-all ${
                    sortBy === 'none' && !isSelectionMode
                      ? 'opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing' 
                      : 'opacity-30 cursor-not-allowed'
                  }`}
                >
                  <GripVertical className="w-4 h-4" />
                </div>
              )}
              
              {/* Badge de difficulté */}
              <DifficultyBadge 
                niveau={skillData.niveau_maitrise_requis || 'Débutant'} 
                skillName={skill}
              />
              <div className="w-px h-4 bg-gray-300"></div>
              
              {/* Nom de la compétence */}
              <span className={`font-medium flex-1 text-sm ${
                isSelected ? 'text-[#18574F]' : 'text-gray-900'
              }`}>{skill}</span>
            </div>
            
            {/* Actions */}
            <div className="flex items-center space-x-1">
              <BallRating
                skillName={skill}
                rating={skillData.rating || 0}
                onRate={onRate}
              />
              <button 
                onClick={() => onToggleExpanded(skill)} 
                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-[#18574F] transition-all p-0.5 hover:bg-gray-100 rounded"
              >
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              <button 
                onClick={() => onEdit(skill)} 
                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-[#18574F] transition-all p-0.5 hover:bg-gray-100 rounded"
              >
                <Edit3 className="w-3 h-3" />
              </button>
              <button 
                onClick={() => onRemove(skill)} 
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all p-0.5 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Détails étendus */}
          {isExpanded && (
            <SkillDetails skillName={skill} data={skillData} />
          )}
        </div>
      )}
    </Draggable>
  );
};